import type {
  EmpresaCaptura,
  FaixaCaptura,
  NotaRecebida,
} from '../../../api/tipos';

type Tom = 'ok' | 'alerta' | 'erro' | 'neutro' | 'info';

const DIA_MS = 24 * 3600 * 1000;

/** Dias até o vencimento do certificado (negativo = vencido). */
export function diasParaVencer(validoAte: string): number {
  return Math.ceil((new Date(validoAte).getTime() - Date.now()) / DIA_MS);
}

/** Certificado perto do fim: a partir daqui pedimos para renovar. */
export const DIAS_AVISO_CERTIFICADO = 30;

/** Data curta (`09/10/2026`) de um ISO. */
export function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** Etiqueta de validade de um certificado A1. */
export function etiquetaCertificado(validoAte: string): {
  tom: Tom;
  texto: string;
} {
  const dias = diasParaVencer(validoAte);
  if (dias <= 0) return { tom: 'erro', texto: 'Vencido' };
  if (dias <= DIAS_AVISO_CERTIFICADO)
    return {
      tom: 'alerta',
      texto: dias === 1 ? 'Vence amanhã' : `Vence em ${dias} dias`,
    };
  return { tom: 'ok', texto: 'Válido' };
}

/** Etiqueta de situação de uma empresa monitorada. */
export function etiquetaEmpresa(e: EmpresaCaptura): {
  tom: Tom;
  texto: string;
} {
  if (!e.ativa) {
    if (e.motivoPausa === 'SALDO')
      return { tom: 'erro', texto: 'Pausada · sem saldo' };
    if (e.motivoPausa === 'CERTIFICADO')
      return { tom: 'erro', texto: 'Pausada · certificado' };
    return { tom: 'neutro', texto: 'Pausada' };
  }
  if (e.ultimoErro) return { tom: 'alerta', texto: 'Ativa · com aviso' };
  return { tom: 'ok', texto: 'Capturando' };
}

/**
 * Estado do XML de uma nota, do ponto de vista de quem quer baixá-lo. É a
 * pergunta que o cliente faz ("posso baixar?"), não o vocabulário da SEFAZ.
 */
export function estadoXml(n: NotaRecebida): {
  tom: Tom;
  texto: string;
  dica: string;
} {
  if (n.disponivel) {
    return {
      tom: 'ok',
      texto: 'Disponível',
      dica: 'XML completo pronto para baixar.',
    };
  }
  if (n.situacao !== 'AUTORIZADA') {
    return {
      tom: 'neutro',
      texto: 'Só resumo',
      dica: 'Nota cancelada ou denegada antes de o XML completo chegar.',
    };
  }
  switch (n.manifestacao) {
    case 'CIENCIA':
      return {
        tom: 'info',
        texto: 'Aguardando XML',
        dica: 'Ciência enviada à SEFAZ. O XML completo chega numa das próximas consultas.',
      };
    case 'PENDENTE':
      return {
        tom: 'info',
        texto: 'Na fila',
        dica: 'A Ciência da Operação será enviada na próxima consulta.',
      };
    case 'FORA_DO_PRAZO':
      return {
        tom: 'alerta',
        texto: 'Manifestar no ERP',
        dica: 'Passou do prazo de 10 dias da Ciência. Faça a manifestação (Confirmação) pelo seu sistema emissor; o XML completo chega depois.',
      };
    case 'ERRO':
      return {
        tom: 'erro',
        texto: 'Erro na Ciência',
        dica: n.manifestacaoErro ?? 'A SEFAZ recusou a Ciência da Operação.',
      };
    default:
      return {
        tom: 'neutro',
        texto: 'Só resumo',
        dica: 'Manifestação automática desligada para esta empresa.',
      };
  }
}

export const SITUACAO_NOTA: Record<
  NotaRecebida['situacao'],
  { tom: Tom; texto: string }
> = {
  AUTORIZADA: { tom: 'ok', texto: 'Autorizada' },
  CANCELADA: { tom: 'erro', texto: 'Cancelada' },
  DENEGADA: { tom: 'neutro', texto: 'Denegada' },
};

/**
 * Faixa do plano para `cnpjs` CNPJs ativos — mesma conta do backend
 * (`precoPlano`). `ate: null` = acima da última faixa, pago por CNPJ.
 */
export function faixaDoPlano(
  cnpjs: number,
  faixas: FaixaCaptura[],
  adicionalCentavos: number,
): { ate: number | null; centavos: number } {
  const faixa = faixas.find((f) => cnpjs <= f.ate);
  if (faixa) return faixa;
  const ultima = faixas[faixas.length - 1];
  return {
    ate: null,
    centavos: ultima.centavos + (cnpjs - ultima.ate) * adicionalCentavos,
  };
}

/** "Até 10 CNPJs", "1 CNPJ" — o nome da faixa para o cliente. */
export function nomeDaFaixa(ate: number | null, cnpjs: number): string {
  if (ate === null) return `${cnpjs} CNPJs`;
  return ate === 1 ? '1 CNPJ' : `Até ${ate} CNPJs`;
}

/** "R$ 1.500,50" a partir do decimal em texto que a API devolve. */
export function valorNota(valor: string): string {
  const n = Number(valor);
  return Number.isFinite(n)
    ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : valor;
}

/** CNPJ formatado, admitindo o alfanumérico. */
export function cnpjLegivel(cnpj: string): string {
  return /^[0-9A-Z]{14}$/.test(cnpj)
    ? `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
    : cnpj;
}
